<script setup lang="ts">
// SetupWizardView — 初始化向导 (Round 10)
// 四步: 环境检测 → 节点设置(名称必填, 用户自定义) → 管理员账号 → 完成。
// 设计与 design/setup-wizard-preview.html (已确认的 v2 稿) 保持一致。
import { computed, onMounted, reactive, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useMutation } from '@tanstack/vue-query'
import { getSystemInfo, postSetupInit, type SystemInfo } from '@/api/setup'
import { login } from '@/api/auth'
import { passkeyBeginRegistration, passkeyFinishRegistration, createPasskey, probePasskeySupport } from '@/api/passkey'
import { useAuthStore } from '@/stores/auth'
import { useNotificationStore } from '@/stores/notification'

const { t } = useI18n()
const router = useRouter()
const route = useRoute()
const auth = useAuthStore()
const toast = useNotificationStore()

const step = ref<1 | 2 | 3 | 4>(1)
const info = ref<SystemInfo | null>(null)
const loadError = ref('')
const passkeySupported = ref(false)

const form = reactive({
  nodeName: '',
  downloadDir: '',
  username: 'admin',
  password: '',
  password2: '',
  wantPasskey: true,
})

const showPwd = ref(false)
const submitError = ref('')
const nodeNameError = ref('')
const passwordMismatch = computed(() => form.password2.length > 0 && form.password !== form.password2)

const pwScore = computed(() => {
  const pw = form.password
  if (!pw) return 0
  let s = 0
  if (pw.length >= 8) s++
  if (pw.length >= 12) s++
  if (/[a-zA-Z]/.test(pw) && /\d/.test(pw)) s++
  if (/[^a-zA-Z0-9]/.test(pw)) s++
  return s
})

const step2Valid = computed(() => form.nodeName.trim().length > 0 && form.nodeName.trim().length <= 64)
const step3Valid = computed(
  () => form.username.trim().length >= 3 && form.password.length >= 8 && form.password === form.password2,
)

function fmtBytes(n: number): string {
  if (!n || n <= 0) return '—'
  const gb = n / 1024 / 1024 / 1024
  if (gb >= 1) return `${gb.toFixed(1)} GB`
  return `${(n / 1024 / 1024).toFixed(0)} MB`
}

const listenAddr = computed(() => {
  if (!info.value) return ''
  const l = info.value.listen
  const shownHost = l.host === '0.0.0.0' || l.host === '::' ? window.location.hostname : l.host
  return `${l.proto}://${shownHost}:${l.port}`
})

const featureLabels: Record<string, string> = {
  bt: 'BT',
  checksum: '校验和',
  'async-dns': 'Async DNS',
  'get-notification': '通知',
  'grep-download': 'Grep',
  metalink: 'Metalink',
  'xml-rpc': 'XML-RPC',
  'rpc-over-websocket': 'WebSocket RPC',
}

onMounted(async () => {
  passkeySupported.value = (await probePasskeySupport()).available
  try {
    info.value = await getSystemInfo()
    if (info.value.initialized) {
      // 已初始化的节点不允许再走向导
      router.replace((route.query.redirect as string | undefined) ?? '/dashboard')
      return
    }
    form.downloadDir = info.value.disk.path || '/data/TrAf'
    auth.setAccountConfigured(false)
  } catch (e) {
    loadError.value = (e as Error).message || t('setup.loadError')
  }
})

function goStep(n: 1 | 2 | 3 | 4) {
  step.value = n
}

const initMutation = useMutation({
  mutationFn: async () => {
    const resp = await postSetupInit({
      node_name: form.nodeName.trim(),
      username: form.username.trim(),
      password: form.password,
      download_dir: form.downloadDir.trim() || undefined,
    })
    // 初始化成功 → 立即登录拿 SPA token (后端已种 session cookie)
    const loginResp = await login({ username: form.username.trim(), password: form.password })
    return { resp, loginResp }
  },
  onSuccess: ({ loginResp }) => {
    auth.setAccountConfigured(true)
    auth.setAuth(loginResp.user, loginResp.token)
    toast.success(t('setup.done.title'))
    step.value = 4
  },
  onError: (err: Error) => {
    submitError.value = err.message || t('setup.loadError')
    toast.error(submitError.value)
  },
})

async function submitInit(): Promise<void> {
  submitError.value = ''
  if (!step2Valid.value || !step3Valid.value) return
  initMutation.mutate(undefined)
}

async function registerPasskey(): Promise<void> {
  if (!passkeySupported.value) return
  try {
    const begin = await passkeyBeginRegistration(`${form.nodeName.trim()} · wizard`)
    const created = await createPasskey(begin)
    await passkeyFinishRegistration({
      id: created.id,
      attestationObject: created.attestationObject,
      clientDataJSON: created.clientDataJSON,
      label: `${form.nodeName.trim()} · wizard`,
    })
    toast.success(t('setup.done.passkeyOk'))
  } catch (e) {
    toast.error((e as Error).message || t('setup.done.passkeyFail'))
  }
}

function enterConsole(): void {
  // initMutation.onSuccess 里已 setAuth; 若 token 为空由路由守卫回登录页
  router.push('/dashboard').catch(() => {
    /* ignore */
  })
}
</script>

<template>
  <div class="wizard-page">
    <div class="wizard">
      <div class="brand">
        <div class="logo">AFD</div>
        <div>
          <div class="brand-name">{{ t('setup.brand') }}</div>
          <div class="brand-sub">{{ t('setup.brandSub') }}</div>
        </div>
      </div>

      <div class="stepper">
        <div class="step" :class="{ active: step === 1, done: step > 1 }">
          <div class="dot">1</div><div class="lbl">{{ t('setup.steps.env') }}</div>
        </div>
        <div class="bar" />
        <div class="step" :class="{ active: step === 2, done: step > 2 }">
          <div class="dot">2</div><div class="lbl">{{ t('setup.steps.node') }}</div>
        </div>
        <div class="bar" />
        <div class="step" :class="{ active: step === 3, done: step > 3 }">
          <div class="dot">3</div><div class="lbl">{{ t('setup.steps.admin') }}</div>
        </div>
        <div class="bar" />
        <div class="step" :class="{ active: step === 4 }">
          <div class="dot">4</div><div class="lbl">{{ t('setup.steps.done') }}</div>
        </div>
      </div>

      <!-- Step 1: 环境检测 -->
      <div v-if="step === 1" class="panel">
        <h2>{{ t('setup.env.title') }}</h2>
        <p class="desc">{{ t('setup.env.desc') }}</p>
        <div v-if="loadError" class="error">{{ loadError }}</div>
        <template v-if="info">
          <div class="section-title">{{ t('setup.env.runtime') }}</div>
          <div class="sysgrid">
            <div class="sysrow"><span class="k">📦 {{ t('setup.env.version') }}</span><span class="v">{{ info.version }} · {{ info.commit }}</span></div>
            <div class="sysrow"><span class="k">🔨 {{ t('setup.env.build') }}</span><span class="v">{{ info.go_version }} · {{ info.build_time }}</span></div>
            <div class="sysrow"><span class="k">💻 {{ t('setup.env.machine') }}</span><span class="v">{{ info.os }}/{{ info.arch }} · {{ info.cpu_count }}C · {{ fmtBytes(info.mem_total || Math.round(info.mem_sys_mb * 1024 * 1024)) }}</span></div>
            <div class="sysrow"><span class="k">💾 {{ t('setup.env.disk') }}</span><span class="v">{{ info.disk.ok ? `${fmtBytes(info.disk.total)} / ${fmtBytes(info.disk.available)}` : '—' }}</span></div>
            <div class="sysrow"><span class="k">🌐 {{ t('setup.env.listen') }}</span><span class="v">{{ info.listen.host }}:{{ info.listen.port }} · {{ info.listen.proto.toUpperCase() }}</span></div>
            <div class="sysrow"><span class="k">🆔 {{ t('setup.env.nodeId') }}</span><span class="v">{{ info.node_id }}</span></div>
          </div>
          <div class="section-title">{{ t('setup.env.features') }}</div>
          <div class="chips">
            <span v-for="f in info.features" :key="f" class="chip">{{ featureLabels[f] ?? f }}</span>
          </div>
        </template>
        <div class="actions">
          <button class="btn primary" :disabled="!info" @click="goStep(2)">
            {{ t('setup.env.start') }} →
          </button>
        </div>
      </div>

      <!-- Step 2: 节点设置 -->
      <div v-else-if="step === 2" class="panel">
        <h2>{{ t('setup.node.title') }}</h2>
        <p class="desc">{{ t('setup.node.desc') }}</p>
        <div class="field">
          <label>{{ t('setup.node.name') }} <span class="req">*</span></label>
          <input
            v-model="form.nodeName"
            type="text"
            :placeholder="t('setup.node.namePlaceholder')"
            @input="nodeNameError = ''"
          >
          <div v-if="nodeNameError" class="error">{{ nodeNameError }}</div>
          <div class="hint">{{ t('setup.node.nameHint') }}</div>
        </div>
        <div class="field">
          <label>{{ t('setup.node.dir') }}</label>
          <input v-model="form.downloadDir" type="text">
          <div class="hint">
            {{ t('setup.node.dirHint', { free: info?.disk.ok ? fmtBytes(info.disk.available) : '—' }) }}
          </div>
        </div>
        <div class="actions">
          <button class="btn" @click="goStep(1)">{{ t('setup.common.prev') }}</button>
          <button class="btn primary" :disabled="!step2Valid" @click="goStep(3)">{{ t('setup.common.next') }} →</button>
        </div>
      </div>

      <!-- Step 3: 管理员账号 -->
      <div v-else-if="step === 3" class="panel">
        <h2>{{ t('setup.admin.title') }}</h2>
        <p class="desc">{{ t('setup.admin.desc') }}</p>
        <div class="field">
          <label>{{ t('setup.admin.username') }}</label>
          <input v-model="form.username" type="text">
        </div>
        <div class="field">
          <label>{{ t('setup.admin.password') }}</label>
          <div class="input-wrap">
            <input v-model="form.password" :type="showPwd ? 'text' : 'password'" :placeholder="t('setup.admin.pwPlaceholder')">
            <button class="toggle-pw" type="button" @click="showPwd = !showPwd">👁</button>
          </div>
          <div class="strength"><i v-for="i in 4" :key="i" :style="{ background: i <= pwScore ? ['', '#f87171', '#fbbf24', '#60a5fa', '#34d399'][pwScore] : undefined }" /></div>
          <div class="hint">{{ t('setup.admin.strength') }}: {{ ['', t('setup.admin.s1'), t('setup.admin.s2'), t('setup.admin.s3'), t('setup.admin.s4')][pwScore] || t('setup.admin.strength') }}</div>
        </div>
        <div class="field">
          <label>{{ t('setup.admin.confirm') }}</label>
          <input v-model="form.password2" :type="showPwd ? 'text' : 'password'" :placeholder="t('setup.admin.confirmPlaceholder')">
          <div v-if="passwordMismatch" class="error">{{ t('setup.admin.mismatch') }}</div>
        </div>
        <label class="check-row">
          <input v-model="form.wantPasskey" type="checkbox">
          <span>{{ t('setup.admin.passkeyOpt') }}</span>
        </label>
        <div v-if="submitError" class="error">{{ submitError }}</div>
        <div class="actions">
          <button class="btn" :disabled="initMutation.isPending.value" @click="goStep(2)">{{ t('setup.common.prev') }}</button>
          <button class="btn primary" :disabled="!step3Valid || initMutation.isPending.value" @click="submitInit()">
            {{ initMutation.isPending.value ? t('setup.admin.working') : t('setup.admin.finish') }}
          </button>
        </div>
      </div>

      <!-- Step 4: 完成 -->
      <div v-else class="panel">
        <div class="success-icon">✓</div>
        <h2>{{ t('setup.done.title') }}</h2>
        <p class="desc">{{ t('setup.done.desc', { name: form.nodeName.trim() }) }}</p>
        <div class="summary">
          <div class="srow"><span class="k">{{ t('setup.node.name') }}</span><span class="v">{{ form.nodeName.trim() }}</span></div>
          <div class="srow"><span class="k">{{ t('setup.admin.username') }}</span><span class="v">{{ form.username.trim() }}</span></div>
          <div class="srow"><span class="k">{{ t('setup.node.dir') }}</span><span class="v">{{ form.downloadDir.trim() }}</span></div>
          <div class="srow"><span class="k">{{ t('setup.done.console') }}</span><span class="v">{{ listenAddr }}</span></div>
          <div class="srow"><span class="k">JSON-RPC</span><span class="v">{{ listenAddr }}/jsonrpc</span></div>
        </div>
        <div class="actions">
          <button v-if="form.wantPasskey && passkeySupported" class="btn" @click="registerPasskey()">
            {{ t('setup.done.passkey') }}
          </button>
          <button class="btn primary" @click="enterConsole()">{{ t('setup.done.enter') }} →</button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.wizard-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: hsl(228 32% 6%);
  padding: 28px 16px;
}
.wizard {
  position: relative;
  width: 480px;
  max-width: 100%;
  background: rgba(255, 255, 255, 0.055);
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 20px;
  backdrop-filter: blur(24px) saturate(1.4);
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.45);
  padding: 32px 36px 28px;
  color: hsl(228 20% 92%);
}
.brand { display: flex; align-items: center; gap: 12px; justify-content: center; }
.logo {
  width: 44px; height: 44px; border-radius: 12px;
  display: flex; align-items: center; justify-content: center;
  background: linear-gradient(135deg, hsl(228 99% 71%), hsl(228 65% 54%));
  font-weight: 800; font-size: 15px; color: #fff;
  box-shadow: 0 6px 18px rgba(77, 107, 254, 0.35);
}
.brand-name { font-size: 19px; font-weight: 700; }
.brand-sub { font-size: 12px; color: hsl(228 10% 62%); margin-top: 2px; }

.stepper { display: flex; align-items: center; justify-content: center; margin: 24px 0; }
.step { display: flex; align-items: center; gap: 7px; }
.dot {
  width: 25px; height: 25px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 12px; font-weight: 600; color: hsl(228 8% 48%);
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.09);
}
.lbl { font-size: 12px; color: hsl(228 8% 48%); }
/* v3.13.81: 窄屏 stepper 只留数字圆点, label 与连接条隐藏 (防横向溢出裁切) */
@media (max-width: 520px) {
  .stepper { margin: 16px 0; }
  .stepper .lbl { display: none; }
  .stepper .bar { width: 14px; margin: 0 5px; }
}
.bar { width: 26px; height: 1.5px; background: rgba(255, 255, 255, 0.14); margin: 0 9px; }
.step.active .dot {
  background: #4d6bfe; border-color: hsl(228 99% 71%); color: #fff;
  box-shadow: 0 0 12px rgba(77, 107, 254, 0.35);
}
.step.active .lbl { color: hsl(228 20% 92%); }
.step.done .dot {
  background: rgba(52, 211, 153, 0.15);
  border-color: rgba(52, 211, 153, 0.4); color: #34d399;
}
.step.done .lbl { color: hsl(228 10% 62%); }

h2 { font-size: 17px; font-weight: 700; text-align: center; margin-bottom: 7px; }
.desc { font-size: 13px; color: hsl(228 10% 62%); text-align: center; line-height: 1.7; margin-bottom: 20px; }

.section-title { font-size: 11.5px; color: hsl(228 8% 48%); letter-spacing: 0.12em; margin: 16px 0 8px; }
.sysgrid { border: 1px solid rgba(255, 255, 255, 0.09); border-radius: 13px; overflow: hidden; background: rgba(255, 255, 255, 0.025); }
.sysrow { display: flex; justify-content: space-between; align-items: center; padding: 8.5px 14px; font-size: 12.5px; }
.sysrow + .sysrow { border-top: 1px solid rgba(255, 255, 255, 0.09); }
.sysrow .k { color: hsl(228 10% 62%); }
.sysrow .v {
  color: hsl(228 20% 92%); font-weight: 600;
  font-family: Consolas, 'Courier New', monospace;
  max-width: 250px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.chips { display: flex; flex-wrap: wrap; gap: 7px; }
.chip {
  font-size: 11.5px; color: hsl(228 10% 62%);
  padding: 4px 11px; border-radius: 999px;
  background: rgba(255, 255, 255, 0.07);
  border: 1px solid rgba(255, 255, 255, 0.09);
}

.field { margin-bottom: 15px; }
label { display: block; font-size: 12.5px; color: hsl(228 10% 62%); margin-bottom: 7px; }
.req { color: hsl(228 99% 71%); margin-left: 3px; }
.input-wrap { position: relative; }
input[type='text'], input[type='password'] {
  width: 100%; padding: 11px 13px; border-radius: 11px; font-size: 14px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.09);
  color: hsl(228 20% 92%); outline: none;
}
input[type='text']:focus, input[type='password']:focus {
  border-color: hsl(228 99% 71%);
  box-shadow: 0 0 0 3px rgba(77, 107, 254, 0.18);
}
.toggle-pw {
  position: absolute; right: 10px; top: 50%; transform: translateY(-50%);
  background: none; border: none; color: hsl(228 8% 48%); cursor: pointer;
}
.hint { font-size: 11.5px; color: hsl(228 8% 48%); margin-top: 6px; line-height: 1.5; }
.error { font-size: 12px; color: #f87171; margin-top: 7px; }
.strength { display: flex; gap: 5px; margin-top: 8px; }
.strength i { flex: 1; height: 3.5px; border-radius: 2px; background: rgba(255, 255, 255, 0.1); }
.check-row { display: flex; align-items: flex-start; gap: 10px; margin: 2px 0 4px; cursor: pointer; }
.check-row input { width: auto; accent-color: #4d6bfe; margin-top: 2px; }
.check-row span { font-size: 12.5px; color: hsl(228 10% 62%); line-height: 1.5; }

.actions { display: flex; gap: 10px; margin-top: 22px; }
.btn {
  flex: 1; padding: 11.5px; border-radius: 11px; font-size: 14px; font-weight: 600;
  cursor: pointer; border: 1px solid rgba(255, 255, 255, 0.09);
  background: rgba(255, 255, 255, 0.07); color: hsl(228 20% 92%);
}
.btn:hover { background: rgba(255, 255, 255, 0.1); }
.btn.primary {
  background: linear-gradient(135deg, hsl(228 99% 71%), hsl(228 65% 54%));
  border: none; color: #fff; box-shadow: 0 6px 18px rgba(77, 107, 254, 0.35);
}
.btn.primary:hover { filter: brightness(1.1); }
.btn:disabled { filter: grayscale(0.6) brightness(0.7); cursor: not-allowed; box-shadow: none; }

.success-icon {
  width: 62px; height: 62px; border-radius: 50%; margin: 2px auto 14px;
  background: rgba(52, 211, 153, 0.12);
  border: 1px solid rgba(52, 211, 153, 0.35);
  display: flex; align-items: center; justify-content: center;
  font-size: 28px; color: #34d399;
}
.summary { border: 1px solid rgba(255, 255, 255, 0.09); border-radius: 12px; overflow: hidden; margin-bottom: 6px; }
.srow { display: flex; justify-content: space-between; padding: 9px 14px; font-size: 12.5px; }
.srow + .srow { border-top: 1px solid rgba(255, 255, 255, 0.09); }
.srow .k { color: hsl(228 10% 62%); }
.srow .v {
  color: hsl(228 20% 92%); font-weight: 600;
  font-family: Consolas, monospace;
  max-width: 260px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
</style>
